import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Breadcrumb from "@/components/Breadcrumb";
import { Search, X, ArrowLeftRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useSeo } from "@/lib/seo";

const PAGE_SIZE = 20;

export default function Trade() {
  useSeo({
    title: "Trade Market — Your Classic Style",
    description: "Swap classic & vintage pieces with fellow members. Browse items open to trade across every department on Your Classic Style.",
  });
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(params.get("q") || "");

  const q = params.get("q") || "";
  const page = Math.max(1, parseInt(params.get("page") || "1", 10));
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  useEffect(() => { setSearchInput(params.get("q") || ""); }, [params]);

  useEffect(() => {
    setLoading(true);
    const base = { tradeable: true };
    if (q) base.q = q;
    Promise.all([
      api.get("/listings", { params: { ...base, limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE } }),
      api.get("/listings/count", { params: base }),
    ])
      .then(([lr, cr]) => { setItems(lr.data); setCount(cr.data.count || 0); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const setFilter = (k, v) => {
    const p = new URLSearchParams(params);
    if (!v) p.delete(k); else p.set(k, v);
    p.delete("page");
    setParams(p);
  };

  const submitSearch = (e) => { e.preventDefault(); setFilter("q", searchInput.trim() || null); };

  const goPage = (n) => {
    const p = new URLSearchParams(params);
    if (n <= 1) p.delete("page"); else p.set("page", String(n));
    setParams(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const pageWindow = () => {
    const w = [];
    const start = Math.max(1, Math.min(page - 2, totalPages - 4));
    const end = Math.min(totalPages, start + 4);
    for (let i = start; i <= end; i++) w.push(i);
    return w;
  };

  return (
    <div data-testid="trade-page" className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10">
      <Breadcrumb className="mb-5" items={[{ label: "Home", to: "/" }, { label: "Trade" }]} />
      <div className="hairline-b pb-6 flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="label-eyebrow inline-flex items-center gap-2" style={{ color: "#1E7A3B" }}>
            <ArrowLeftRight size={13} /> Open to trades
          </div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">
            {q ? `“${q}”.` : "Trade Market."}
          </h1>
          <p className="mt-3 max-w-xl text-neutral-500 text-sm">
            Every piece here has been marked <span style={{ color: "#1E7A3B" }} className="font-medium">open to trades</span> by its
            seller. Find something you love, then propose a swap from the item's page.
          </p>
          {!loading && (
            <div className="mt-3 text-sm text-neutral-500" data-testid="trade-result-count">
              {count} tradeable item{count === 1 ? "" : "s"}{q ? ` matching “${q}”` : ""}
            </div>
          )}
        </div>
        <form onSubmit={submitSearch} className="flex items-center hairline bg-white" data-testid="trade-search-form" role="search">
          <Search size={16} className="ml-3 text-neutral-400 shrink-0" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            data-testid="trade-search-input"
            placeholder="Search tradeable items by name or type…"
            className="bg-transparent px-3 py-2 text-sm outline-none w-full sm:w-80"
          />
          {q && (
            <button type="button" data-testid="trade-search-clear"
              onClick={() => { setSearchInput(""); setFilter("q", null); }}
              className="px-2 text-neutral-400 hover:text-black"><X size={15} /></button>
          )}
          <button type="submit" data-testid="trade-search-btn" className="btn-primary text-[0.68rem] m-1">Search</button>
        </form>
      </div>

      <div className="mt-10">
        {loading ? (
          <div className="text-neutral-500 label-eyebrow">Loading…</div>
        ) : items.length === 0 ? (
          <div data-testid="trade-empty-state" className="hairline p-16 text-center">
            <ArrowLeftRight size={22} className="mx-auto text-neutral-300" />
            <div className="label-eyebrow mt-4">No tradeable items {q ? "match your search" : "yet"}</div>
            <p className="mt-3 text-neutral-500 max-w-md mx-auto">
              {q
                ? <>Nothing tradeable matches “{q}”. Try another keyword or <button className="underline" data-testid="trade-clear-search" onClick={() => { setSearchInput(""); setFilter("q", null); }}>clear your search</button>.</>
                : <>Sellers can mark a listing “open to trades” when creating or editing it. List something on <Link to="/sell" className="underline text-black">Sell</Link> and toggle trades on.</>}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {items.map((it) => <ProductCard key={it.id} item={it} />)}
            </div>
            {totalPages > 1 && (
              <nav className="mt-10 flex items-center justify-center gap-1.5" data-testid="trade-pagination">
                <button className="chip" data-testid="trade-page-prev" disabled={page <= 1} onClick={() => goPage(page - 1)}>
                  <ChevronLeft size={14} /> Prev
                </button>
                {pageWindow()[0] > 1 && (
                  <>
                    <button className="chip" data-testid="trade-page-1" onClick={() => goPage(1)}>1</button>
                    {pageWindow()[0] > 2 && <span className="px-1 text-neutral-400">…</span>}
                  </>
                )}
                {pageWindow().map((n) => (
                  <button key={n} className="chip" data-active={n === page} data-testid={`trade-page-${n}`} onClick={() => goPage(n)}>{n}</button>
                ))}
                {pageWindow().slice(-1)[0] < totalPages && (
                  <>
                    {pageWindow().slice(-1)[0] < totalPages - 1 && <span className="px-1 text-neutral-400">…</span>}
                    <button className="chip" data-testid={`trade-page-${totalPages}`} onClick={() => goPage(totalPages)}>{totalPages}</button>
                  </>
                )}
                <button className="chip" data-testid="trade-page-next" disabled={page >= totalPages} onClick={() => goPage(page + 1)}>
                  Next <ChevronRight size={14} />
                </button>
              </nav>
            )}
          </>
        )}
      </div>
    </div>
  );
}
